// Storage guard MUST be first so it runs before any module touches Web Storage
// (the Emergent in-app preview webview can block localStorage/sessionStorage).
import "@/storageGuard";
import React from "react";
import ReactDOM from "react-dom/client";
// Sentry MUST be the very first import so it can hook global error handlers
// before any app code runs. No-op if REACT_APP_SENTRY_DSN_FRONTEND is unset.
import "@/sentry";
import "@/index.css";
import App from "@/App";
import { initNativeApp } from "@/lib/native";
import "@/plugins";  // self-register custom-viz plugins (Sankey, etc.)

// ─── Response body re-read safety net ─────────────────────────────────────
// The Emergent platform injects an `emergent-main.js` fetch interceptor that
// reads response bodies for telemetry. Once a fetch Response body is read,
// calling `.text()` / `.json()` a second time throws "body stream already
// read". We patch Response.prototype so EVERY caller in the app (including
// the many raw `fetch().then(r => r.json())` sites we inherited) transparently
// reads from a fresh clone — no per-call migration needed.
(() => {
  if (typeof Response === 'undefined' || !Response.prototype) return;
  const origText = Response.prototype.text;
  const origJson = Response.prototype.json;
  Response.prototype.text = function safeText() {
    // Clone yields a fresh stream; if the original was already consumed by
    // another interceptor, clone may refuse — fall back to the original and
    // swallow the "already read" error as an empty string so callers don't
    // see a mystery stack trace.
    try {
      return origText.call(this.clone());
    } catch (_) {
      try { return origText.call(this); } catch (_) { return Promise.resolve(''); }
    }
  };
  Response.prototype.json = function safeJson() {
    return this.text().then(txt => {
      if (!txt) return null;
      try { return JSON.parse(txt); } catch (_) { return null; }
    });
  };
})();

// ─── CSRF token auto-attach ────────────────────────────────────────────────
// Wrap the global fetch so any credentialed POST/PUT/PATCH/DELETE call
// automatically echoes the csrf_token cookie back as an X-CSRF-Token header.
// This keeps every file that uses raw fetch() (Layout.js, AuthContext.js,
// QuickstartPage.js, etc.) compatible with the backend's double-submit
// CSRF middleware without forcing a per-call code change.
(() => {
  const _origFetch = window.fetch.bind(window);
  const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
  const readCsrf = () => {
    const m = document.cookie.split('; ').find(r => r.startsWith('csrf_token='));
    return m ? decodeURIComponent(m.split('=')[1]) : '';
  };
  window.fetch = (resource, init = {}) => {
    const method = (init.method || (resource && resource.method) || 'GET').toUpperCase();
    if (MUTATING.has(method) && (init.credentials === 'include' || init.credentials === 'same-origin')) {
      const csrf = readCsrf();
      if (csrf) {
        const existing = init.headers || {};
        const headers = existing instanceof Headers
          ? (() => { const h = new Headers(existing); if (!h.has('X-CSRF-Token')) h.set('X-CSRF-Token', csrf); return h; })()
          : { ...existing, 'X-CSRF-Token': existing['X-CSRF-Token'] || existing['x-csrf-token'] || csrf };
        init = { ...init, headers };
      }
    }
    return _origFetch(resource, init).then(res => {
      // Trial-expired soft-lock: backend returns 402 on writes for expired
      // trial tenants. Surface a single global event so the Layout can show
      // a banner + toast without every individual call having to handle it.
      if (res && res.status === 402 && typeof res.clone === 'function') {
        try {
          res.clone().json().then(body => {
            if (body && body.trial_expired) {
              window.dispatchEvent(new CustomEvent('trial-expired', { detail: body }));
            }
          }).catch(() => {});
        } catch { /* no-op */ }
      }
      return res;
    });
  };
})();

// Suppress browser-injected errors (Brave/Firefox on iOS injects __firefox__)
// plus the benign "ResizeObserver loop completed…" notice that cmdk/Radix
// fires while a filtered list resizes (harmless; only the dev overlay surfaces it).
// Must catch both 'error' and 'unhandledrejection' events, and override console.error
const _browserErrorPattern = /__firefox__|__crWeb|webkit\.messageHandlers|ResizeObserver loop/;
// A contentless cross-origin "Script error." (no error object, no real source)
// is what the Emergent in-app preview webview surfaces for its own injected /
// embedded scripts. It carries zero actionable detail and never originates from
// our same-origin bundle (those arrive with a real message + stack), so it is
// safe to swallow — it only spuriously triggers the dev error overlay.
const _isContentlessScriptError = (message, source, error) =>
  String(message || '').trim() === 'Script error.' && !error && !source;
window.addEventListener('error', (e) => {
  if (_browserErrorPattern.test(e.message || '') || _browserErrorPattern.test(e.filename || '')
      || _isContentlessScriptError(e.message, e.filename, e.error)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return true;
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  if (_browserErrorPattern.test(String(e.reason || ''))) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);
// Patch console.error to suppress browser-injected noise
const _origConsoleError = console.error;
console.error = (...args) => {
  if (args.some(a => _browserErrorPattern.test(String(a || '')))) return;
  _origConsoleError.apply(console, args);
};
// Also intercept window.onerror (used by react-error-overlay)
const _origOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (_browserErrorPattern.test(String(message || '')) || _browserErrorPattern.test(String(source || ''))
      || _isContentlessScriptError(message, source, error)) return true;
  if (_origOnError) return _origOnError(message, source, lineno, colno, error);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Initialize native Capacitor plugins (no-op on web)
initNativeApp();

// Register PWA Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => console.log('SW registered:', reg.scope),
      (err) => console.log('SW registration failed:', err)
    );
  });
}
